/**
 * Unit converter: V, A, Ω, F, H, Hz, s with prefix dropdowns.
 * Input can be plain number or with suffix (e.g. 1u, 10k) for convenience.
 */

import React, { useState, useMemo } from 'react';
import { parseEngineering, inputClass, labelClass, hintClass } from './parseEngineering';

const ts = () => new Date().toISOString();

const PREFIXES = [
  { key: 'p', name: 'pico', mult: 1e-12 },
  { key: 'n', name: 'nano', mult: 1e-9 },
  { key: 'µ', name: 'micro', mult: 1e-6 },
  { key: 'm', name: 'milli', mult: 1e-3 },
  { key: '', name: '(base)', mult: 1 },
  { key: 'k', name: 'kilo', mult: 1e3 },
  { key: 'M', name: 'mega', mult: 1e6 },
  { key: 'G', name: 'giga', mult: 1e9 },
];

const UNITS = [
  { id: 'V', name: 'Voltage', base: 'V' },
  { id: 'A', name: 'Current', base: 'A' },
  { id: 'Ohm', name: 'Resistance', base: 'Ω' },
  { id: 'F', name: 'Capacitance', base: 'F' },
  { id: 'H', name: 'Inductance', base: 'H' },
  { id: 'Hz', name: 'Frequency', base: 'Hz' },
  { id: 's', name: 'Time', base: 's' },
];

const selectClass = 'px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none';

const UnitConverter = () => {
  const [unitType, setUnitType] = useState('Ohm');
  const [fromPrefix, setFromPrefix] = useState(4);
  const [toPrefix, setToPrefix] = useState(4);
  const [inputVal, setInputVal] = useState('');

  console.log(`[${ts()}] [UnitConverter] render`);

  const unit = useMemo(() => UNITS.find((u) => u.id === unitType) || UNITS[0], [unitType]);
  const fromMult = PREFIXES[fromPrefix].mult;
  const toMult = PREFIXES[toPrefix].mult;

  // Accept plain number or engineering (e.g. 1u, 10k) – value in base SI
  const valueBase = useMemo(() => {
    const parsed = parseEngineering(inputVal);
    if (parsed != null) return parsed;
    const v = parseFloat(String(inputVal).trim().replace(',', '.'));
    if (!Number.isFinite(v)) return null;
    return v * fromMult;
  }, [inputVal, fromMult]);

  const result = useMemo(() => {
    if (valueBase == null) return null;
    return valueBase / toMult;
  }, [valueBase, toMult]);

  const fromLabel = `${PREFIXES[fromPrefix].key || '1'}${unit.base}`;
  const toLabel = `${PREFIXES[toPrefix].key || '1'}${unit.base}`;

  return (
    <div className="space-y-4">
      <p className={hintClass}>
        Convert between prefixes. Type a number or use suffix: 1u, 10k, 2.2m, etc.
      </p>
      <div>
        <label className={labelClass}>Quantity</label>
        <select value={unitType} onChange={(e) => setUnitType(e.target.value)} className={selectClass}>
          {UNITS.map((u) => (
            <option key={u.id} value={u.id}>{u.name} ({u.base})</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>From ({fromLabel})</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder="e.g. 1 or 1u or 10k"
              className={`${inputClass} flex-1`}
            />
            <select value={fromPrefix} onChange={(e) => setFromPrefix(Number(e.target.value))} className={`${selectClass} w-24`}>
              {PREFIXES.map((p, i) => (
                <option key={i} value={i}>{p.key || '1'}{unit.base}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>To ({toLabel})</label>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={result != null ? (result < 1e-3 || result >= 1e6 ? result.toExponential(4) : result.toFixed(6)) : '—'}
              className={`${inputClass} flex-1 bg-gray-50`}
            />
            <select value={toPrefix} onChange={(e) => setToPrefix(Number(e.target.value))} className={`${selectClass} w-24`}>
              {PREFIXES.map((p, i) => (
                <option key={i} value={i}>{p.key || '1'}{unit.base}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitConverter;
